// video-recording.service.ts
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class VideoRecordingService {
  
  async recordPageScroll(durationSeconds: number = 5, scrollSpeed: number = 1): Promise<Blob> {
    try {
      // Get ACCURATE full page height
      const body = document.body;
      const html = document.documentElement;
      
      const fullHeight = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.scrollHeight,
        html.offsetHeight
      );
      
      const viewportHeight = window.innerHeight;
      
      // CRITICAL FIX: Ensure we scroll to absolute bottom
      const maxScrollY = fullHeight - viewportHeight;
      const scrollDistance = maxScrollY;
      
      console.log(new Date().toLocaleString() + '  ➤  Recording details:', {
        fullHeight,
        viewportHeight,
        maxScrollY,
        scrollDistance
      });
      
      // Adjust duration based on scroll speed
      const actualDuration = durationSeconds / scrollSpeed;
      
      // Store original scroll position
      const originalScrollY = window.scrollY;
      
      // Scroll to absolute top first
      window.scrollTo(0, 0);
      await this.delay(500);
      
      // Start recording
      const stream = await this.captureScreen();
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 2500000
      });
      
      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.start(100);
      
      // Perform smooth scroll to ABSOLUTE BOTTOM
      await this.smoothScrollToBottom(scrollDistance, actualDuration);
      
      // IMPORTANT: Hold at bottom for a moment
      await this.delay(500);
      
      // Stop recording
      mediaRecorder.stop();
      
      const webmBlob = await new Promise<Blob>((resolve) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          resolve(blob);
        };
      });
      
      stream.getTracks().forEach(track => track.stop());
      window.scrollTo(0, originalScrollY);
      
      return webmBlob;
      
    } catch (error) {
      console.error('Video recording failed:', error);
      throw error;
    }
  }
  
  private async captureScreen(): Promise<MediaStream> {
    try {
      return await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        } as any,
        audio: false,
        preferCurrentTab: true
      } as any);
    } catch (error) {
      console.error('getDisplayMedia failed:', error);
      throw new Error('Screen capture permission denied or not supported');
    }
  }
  
  /**
   * FIXED: Scroll to absolute bottom of page
   */
  private async smoothScrollToBottom(distance: number, durationSeconds: number): Promise<void> {
    const startTime = Date.now();
    const startY = window.scrollY;
    const targetY = startY + distance;
    
    console.log(new Date().toLocaleString() + '  ➤  Scrolling from', startY, 'to', targetY, 'distance:', distance);
    
    return new Promise((resolve) => {
      const scroll = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / (durationSeconds * 1000), 1);
        
        // Linear easing for consistent speed
        const currentScrollY = startY + (distance * progress);
        
        // Ensure we actually reach the target
        if (progress >= 1) {
          window.scrollTo(0, targetY);
          console.log(new Date().toLocaleString() + '  ➤  Scroll complete. Final position:', window.scrollY);
          resolve();
        } else {
          window.scrollTo(0, currentScrollY);
          requestAnimationFrame(scroll);
        }
      };
      
      requestAnimationFrame(scroll);
    });
  }
  
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}